import React, {
  useState,
  useRef,
  useMemo,
  useCallback,
  forwardRef,
  useImperativeHandle,
  useEffect,
} from "react";
import { useSelector } from "react-redux";
import moment from "moment";
import DataGrid from "@dtci/spellbook/DataGrid";
import Banner from "@dtci/spellbook/Banner";
import Typography from "@dtci/spellbook/Typography";
import ContentBlock from "@dtci/spellbook/ContentBlock";
import Button from "@dtci/spellbook/Button";
import { useConfirmationDialog } from "tap/components/dialogs/dialogs/confirmation.jsx";
import { useDialogsActions } from "tap/components/dialogs/hooks.js";
import { useOffersGridUtils } from "./utils/gridUtils";
import { useOffersActions } from "tap/store/offers/useOffers.js";
import { useLoading } from "tap/utils/hooks/useLoading.js";
import { useToasts } from "tap/utils/hooks/useToasts.js";
import { useToastNotification } from "tap/components/ToastNotifications";
import { startJobStatusTracker } from "tap/utils/jobStatusTracker.js";
import { tapApi } from "tap/api.js";
import { buildOfferHistoryHref } from "tap/utils/buildOfferHistoryUrl.js";
import { hasFeature, usePermissions } from "tap/utils/permissions.js";
import { escapeHtml } from "tap/utils/htmlEscape.js";
import ExcelJS from "exceljs";
import FileSaver from "file-saver";
import styles from "../../../../components/AGGrid/details-table.module.scss";
import {
  getMergedColumnOptionsForGrid,
  getOfferTableExcelExportColumns,
} from "./config/columnOptions.js";
import { usePaginatedGridSelection } from "tap/utils/hooks/usePaginatedGridSelection.js";
import { canManageOffers } from "tap/pages/Offers/utils/canManageOffers.js";

/** Save buffer as Excel file */
const saveAsExcelFile = (buffer, fileName) => {
  const EXCEL_TYPE =
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
  const EXCEL_EXTENSION = ".xlsx";
  const data = new Blob([buffer], { type: EXCEL_TYPE });
  FileSaver.saveAs(data, fileName + EXCEL_EXTENSION);
};

/**
 * Export data to Excel using ordered `{ field, header }` definitions from config.
 * @param {Object[]} data - Rows to export
 * @param {string} excelFileName - Base file name (without extension)
 * @param {{ field: string, header: string }[]} columnDefs - Required; from getOfferTableExcelExportColumns
 */
const exportAsExcelFile = async (data, excelFileName, columnDefs) => {
  if (!data || data.length === 0) {
    console.warn("No data to export");
    return;
  }

  if (!columnDefs || columnDefs.length === 0) {
    console.warn("No column definitions for export");
    return;
  }

  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet("Offers");

  sheet.columns = columnDefs.map(({ field, header }) => ({
    header,
    key: field,
  }));

  for (let i = 0; i < data.length; i++) {
    const dataRow = data[i];
    const rowObj = {};
    for (let j = 0; j < columnDefs.length; j++) {
      const { field } = columnDefs[j];
      rowObj[field] = dataRow[field];
    }
    sheet.addRow(rowObj);
  }

  try {
    const buffer = await workbook.xlsx.writeBuffer();
    saveAsExcelFile(buffer, excelFileName);
  } catch (error) {
    console.error("Error writing Excel buffer:", error);
  }
};

/** Detail Cell Renderer for Master Detail - displays the message field */
const MessageDetailCellRenderer = forwardRef((props, ref) => {
  const { data } = props;
  const message = data?.message;

  useImperativeHandle(ref, () => ({
    refresh: () => true,
  }));

  return (
    <div className={styles.detailPanel}>
      <div className={styles.detailLabel}>Message</div>
      {message ? (
        <div className={styles.detailMessage}>{message}</div>
      ) : (
        <div className={styles.detailEmpty}>No message available</div>
      )}
    </div>
  );
});

/** Statuses that allow push to RL */
const PUSHABLE_STATUSES = [
  "PENDING",
  "SYSTEM_UPDATED",
  "SYSTEM_CREATED",
  "USER_MODIFIED",
  "SYSTEM_UPDATED_OFFER_ACTIVE",
  "USER_MODIFIED_OFFER_ACTIVE",
  "RIGHTS_ISSUE",
  "INFO_RESTRICTIONS",
];

/** Sort offer rows by the active grid column and direction */
const sortOfferRows = (rows, sortedColId, sortDirection) => {
  if (!rows || rows.length === 0 || !sortedColId) {
    return rows;
  }

  const isIdField = sortedColId === "id" || sortedColId.endsWith("Id");
  const mult = sortDirection === "asc" ? 1 : -1;

  return [...rows].sort((a, b) => {
    const aVal = a[sortedColId];
    const bVal = b[sortedColId];

    if (aVal == null && bVal == null) return 0;
    if (aVal == null) return mult;
    if (bVal == null) return -mult;

    if (isIdField) {
      return (Number(aVal) - Number(bVal)) * mult;
    }

    if (aVal < bVal) return -mult;
    if (aVal > bVal) return mult;
    return 0;
  });
};

/** Apply filters to offers data (supports multi-select) */
const applyFilters = (offers, filters) => {
  if (!offers || !Array.isArray(offers)) return [];

  const activeFilters = Object.entries(filters).filter(
    ([, values]) => values && values.length > 0,
  );

  if (activeFilters.length === 0) return offers;

  return offers.filter((offer) => {
    return activeFilters.every(([field, selectedOptions]) => {
      const offerValue = offer[field];
      if (offerValue === null || offerValue === undefined) return false;
      return selectedOptions.some(
        (option) => String(offerValue) === String(option.value),
      );
    });
  });
};

/** Validates an offer before saving - returns error message or null */
const DATE_FIELDS = [
  "termStart",
  "termEnd",
  "dateTaken",
  "suppressionLiftDate",
  "preSell",
  "ocad",
];

const validateOffer = (offer) => {
  const errors = [];

  // Validate date fields
  DATE_FIELDS.forEach((field) => {
    if (offer[field] && offer[field] !== "") {
      if (!moment(offer[field]).isValid()) {
        errors.push(`${field} is an invalid date with value ${offer[field]}`);
      }
    }
  });

  if (offer.termStart && offer.termEnd) {
    const start = moment(offer.termStart);
    const end = moment(offer.termEnd);
    if (end.isBefore(start)) {
      errors.push("Term End date must be after Term Start date");
    }
  }

  if (
    offer.percentageSplit !== null &&
    offer.percentageSplit !== undefined &&
    offer.percentageSplit !== ""
  ) {
    const pct = parseFloat(String(offer.percentageSplit).replace("%", ""));
    if (isNaN(pct) || pct < 0 || pct > 100) {
      errors.push("Percentage Split must be between 0 and 100");
    }
  }

  if (
    offer.licenseFee !== null &&
    offer.licenseFee !== undefined &&
    offer.licenseFee !== ""
  ) {
    const fee = parseFloat(offer.licenseFee);
    if (isNaN(fee) || fee < 0) {
      errors.push("License Fee must be a valid non-negative number");
    }
  }

  if (
    offer.minDeemedPrice !== null &&
    offer.minDeemedPrice !== undefined &&
    offer.minDeemedPrice !== ""
  ) {
    const price = parseFloat(offer.minDeemedPrice);
    if (isNaN(price) || price < 0) {
      errors.push("Min Deemed Price must be a valid non-negative number");
    }
  }

  if (
    offer.seasonPassAmt !== null &&
    offer.seasonPassAmt !== undefined &&
    offer.seasonPassAmt !== ""
  ) {
    const amt = parseFloat(offer.seasonPassAmt);
    if (isNaN(amt) || amt < 0) {
      errors.push("Season Pass Amount must be a valid non-negative number");
    }
  }

  return errors.length > 0 ? errors.join(". ") : null;
};

/**
 * Offers Grid - Displays offer table with inline editing, filtering, bulk actions, and export.
 * Supports master-detail for message display and multi-select for bulk push/delete operations.
 */
export const OffersGrid = React.forwardRef(
  (
    {
      searchAll = "",
      hiddenColumns = [],
      paginatedOffers = null,
      filteredOffers: filteredOffersProp = null,
      currentPage = 0,
    },
    ref,
  ) => {
    const gridApiRef = useRef(null);

    const { setLoadingTrue, setLoadingFalse } = useLoading();
    const { openConfirmationDialog } = useConfirmationDialog();
    const { openDialog, closeDialog } = useDialogsActions();
    const { showErrorToast, showSuccessToast } = useToasts();
    const { createJobProgressToastHandler } = useToastNotification();

    const {
      offers,
      offerSubDubLanguageOptions,
      selectedJob,
      refreshOffers,
      internalFilters,
    } = useSelector((state) => state.offers);
    const { updateOffers } = useOffersActions();
    const userCanManageOffers = canManageOffers(usePermissions());
    const jobId = selectedJob?.id;

    const getOfferHistoryHref = useCallback(
      (offer) =>
        buildOfferHistoryHref(
          offer?.licensingJobId ?? jobId,
          offer?.id,
          currentPage,
        ),
      [jobId, currentPage],
    );

    const hasEpisodeFeature = hasFeature(
      "TAP_OFFER_EPISODE_TIER_AND_EPISODE_EMA_PRICE_TYPE",
    );

    /** Column options - conditionally includes episode columns based on feature flag */
    const COLUMN_OPTIONS = useMemo(
      () => getMergedColumnOptionsForGrid(hasEpisodeFeature),
      [hasEpisodeFeature],
    );

    const [editingRows, setEditingRows] = useState({});
    const editingRowsRef = useRef(editingRows);
    editingRowsRef.current = editingRows;

    const [editedValues, setEditedValues] = useState({});
    const editedValuesRef = useRef(editedValues);
    editedValuesRef.current = editedValues;

    const [originalValues, setOriginalValues] = useState({});
    const lastSelectedLanguagesRef = useRef({});

    const getEditingRows = () => editingRowsRef.current;
    const getEditedValues = () => editedValuesRef.current;

    const editingRowCount = useMemo(() => {
      return Object.values(editingRows).filter(Boolean).length;
    }, [editingRows]);

    const hasAnyChanges = useMemo(() => {
      const editingRowIds = Object.entries(editingRows)
        .filter(([, isEditing]) => isEditing)
        .map(([id]) => id);

      return editingRowIds.some((id) => {
        const changes = editedValues[id];
        return changes && Object.keys(changes).length > 0;
      });
    }, [editingRows, editedValues]);

    /** Merges pending edits into offers for display without persisting */
    const offersWithEdits = useMemo(() => {
      if (!offers) return [];
      return offers.map((offer) => {
        const edits = editedValues[offer.id];
        return edits ? { ...offer, ...edits } : offer;
      });
    }, [offers, editedValues]);

    /** Applies both dropdown filters and text search to offers */
    const filteredOffersInternal = useMemo(() => {
      let result = applyFilters(offersWithEdits, internalFilters);

      if (searchAll && searchAll.trim() !== "") {
        const lowerSearch = searchAll.toLowerCase().trim();
        result = result.filter((offer) => {
          return Object.values(offer).some((value) => {
            if (value === null || value === undefined) return false;
            return String(value).toLowerCase().includes(lowerSearch);
          });
        });
      }

      return result;
    }, [offersWithEdits, internalFilters, searchAll]);

    // Use filtered offers from parent if provided, otherwise calculate internally
    const filteredOffers =
      filteredOffersProp !== null ? filteredOffersProp : filteredOffersInternal;

    /** Full filtered set with pending inline edits (for export/print) */
    const filteredOffersWithEdits = useMemo(() => {
      return (filteredOffers ?? []).map((offer) => {
        const edits = editedValues[offer.id];
        return edits ? { ...offer, ...edits } : offer;
      });
    }, [filteredOffers, editedValues]);

    /** Row data for the grid: merge pending edits into parent paginated slice */
    const displayOffers = useMemo(() => {
      if (paginatedOffers !== null) {
        return paginatedOffers.map((offer) => {
          const edits = editedValues[offer.id];
          return edits ? { ...offer, ...edits } : offer;
        });
      }
      return filteredOffersInternal;
    }, [paginatedOffers, filteredOffersInternal, editedValues]);

    const refreshOffersList = async () => {
      if (!selectedJob?.id) return;
      try {
        const data = await tapApi.licensingJobOfferTable(selectedJob.id);
        if (data && !data.code && !data.httpCode && !data.error) {
          updateOffers({ offers: data });
        }
      } catch (error) {
        console.error("Failed to refresh offers:", error);
      }
    };

    useEffect(() => {
      if (refreshOffers) {
        refreshOffersList();
        updateOffers({ refreshOffers: false });
      }
    }, [refreshOffers]);

    const onValueChange = useCallback((data, fieldName, newValue) => {
      setEditedValues((prev) => ({
        ...prev,
        [data.id]: {
          ...(prev[data.id] || {}),
          [fieldName]: newValue,
        },
      }));
    }, []);

    const onLanguageSelect = useCallback((offerId, field, value) => {
      lastSelectedLanguagesRef.current[offerId] = {
        ...lastSelectedLanguagesRef.current[offerId],
        [field]: value,
      };
    }, []);

    const getLastSelectedLanguages = useCallback(
      () => lastSelectedLanguagesRef.current,
      [],
    );

    const isTprJob = !!selectedJob?.isTpr;

    const onEditOffer = (offer) => {
      if (isTprJob) {
        return;
      }
      setOriginalValues((prev) => ({
        ...prev,
        [offer.id]: { ...offer },
      }));
      setEditingRows((prev) => ({
        ...prev,
        [offer.id]: true,
      }));
      setEditedValues((prev) => ({
        ...prev,
        [offer.id]: {},
      }));

      const selectedNodes = gridApiRef.current?.getSelectedNodes() || [];
      const selectedIds = selectedNodes.map((node) => node.data?.id);

      const rowNode = gridApiRef.current?.getRowNode(String(offer.id));
      if (rowNode) {
        gridApiRef.current?.refreshCells({ rowNodes: [rowNode], force: true });
      }

      if (selectedIds.length > 0) {
        gridApiRef.current?.forEachNode((node) => {
          if (selectedIds.includes(node.data?.id)) {
            node.setSelected(true);
          }
        });
      }
    };

    const saveOfferInternal = async (offer) => {
      if (!offer?.id) {
        showErrorToast("Invalid offer data.");
        return;
      }

      const changes = editedValuesRef.current[offer.id] || {};
      const updatedOffer = { ...offer, ...changes };

      const validationError = validateOffer(updatedOffer);
      if (validationError) {
        showErrorToast(validationError);
        return;
      }

      setLoadingTrue();
      try {
        const result = await tapApi.updateOfferTable(updatedOffer);

        if (result?.code || result?.httpCode || result?.error) {
          showErrorToast(
            result?.message ||
              result?.error?.message ||
              result?.error ||
              "Failed to save offer.",
          );
          setLoadingFalse();
          return;
        }

        setEditingRows((prev) => ({
          ...prev,
          [offer.id]: false,
        }));

        setEditedValues((prev) => {
          const newState = { ...prev };
          delete newState[offer.id];
          return newState;
        });

        setOriginalValues((prev) => {
          const newState = { ...prev };
          delete newState[offer.id];
          return newState;
        });

        showSuccessToast("Offer Saved");
        await refreshOffersList();
      } catch (error) {
        console.error("Failed to save offer:", error);
        showErrorToast(error?.message || "Failed to save offer.");
      } finally {
        setLoadingFalse();
      }
    };

    const onSaveAll = async () => {
      const editingRowIds = Object.entries(editingRowsRef.current)
        .filter(([, isEditing]) => isEditing)
        .map(([id]) => id);

      if (editingRowIds.length === 0) return;

      setLoadingTrue();
      let successCount = 0;
      let errorCount = 0;

      for (const rowId of editingRowIds) {
        const offer = offers.find((o) => String(o.id) === String(rowId));
        if (!offer) continue;

        const changes = editedValuesRef.current[rowId] || {};
        const updatedOffer = { ...offer, ...changes };

        const validationError = validateOffer(updatedOffer);
        if (validationError) {
          errorCount++;
          continue;
        }

        try {
          const result = await tapApi.updateOfferTable(updatedOffer);
          if (result?.code || result?.httpCode || result?.error) {
            errorCount++;
          } else {
            successCount++;
            setEditingRows((prev) => ({ ...prev, [rowId]: false }));
            setEditedValues((prev) => {
              const newState = { ...prev };
              delete newState[rowId];
              return newState;
            });
            setOriginalValues((prev) => {
              const newState = { ...prev };
              delete newState[rowId];
              return newState;
            });
          }
        } catch (error) {
          console.error(`Failed to save offer ${rowId}:`, error);
          errorCount++;
        }
      }

      setLoadingFalse();
      await refreshOffersList();
      gridApiRef.current?.refreshCells({ force: true });

      if (successCount > 0 && errorCount === 0) {
        showSuccessToast(`${successCount} offer(s) saved successfully.`);
      } else if (successCount > 0 && errorCount > 0) {
        showErrorToast(
          `${successCount} saved, ${errorCount} failed. Check validation errors.`,
        );
      } else if (errorCount > 0) {
        showErrorToast(
          `Failed to save ${errorCount} offer(s). Check validation errors.`,
        );
      }
    };

    /** Shows save dialog when multiple rows being edited, otherwise saves directly */
    const onSaveOffer = (offer) => {
      const editingRowCount = Object.values(editingRowsRef.current).filter(
        Boolean,
      ).length;

      if (editingRowCount <= 1) {
        saveOfferInternal(offer);
        return;
      }

      openDialog({
        title: "Save Offer(s)",
        hideClose: true,
        content: (
          <div>
            <Typography variant="T30" style={{ marginBottom: "24px" }}>
              Are you sure you want to save changes in TAP2? Unsaved changes
              will be lost.
            </Typography>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Button
                type="button"
                styleType="link"
                onClick={() => closeDialog()}
              >
                Cancel
              </Button>
              <div style={{ display: "flex", gap: "12px" }}>
                <Button
                  type="button"
                  styleType="standard"
                  onClick={() => {
                    closeDialog();
                    saveOfferInternal(offer);
                  }}
                >
                  Save This Offer
                </Button>
                <Button
                  type="button"
                  styleType="promoted"
                  onClick={() => {
                    closeDialog();
                    onSaveAll();
                  }}
                >
                  Save All Offers
                </Button>
              </div>
            </div>
          </div>
        ),
      });
    };

    const onCancelEdit = (offer) => {
      setEditingRows((prev) => ({
        ...prev,
        [offer.id]: false,
      }));
      setEditedValues((prev) => {
        const newState = { ...prev };
        delete newState[offer.id];
        return newState;
      });
      setOriginalValues((prev) => {
        const newState = { ...prev };
        delete newState[offer.id];
        return newState;
      });
    };

    const onDiscardAll = () => {
      setEditingRows({});
      setEditedValues({});
      setOriginalValues({});
    };

    const onDeleteOffer = (offer) => {
      openConfirmationDialog(
        "Delete Offer(s)",
        "Yes",
        [
          "Are you sure you want to delete the offer(s) from TAP2 and Rightsline? This action cannot be undone.",
        ],
        async () => {
          setLoadingTrue();
          try {
            const result = await tapApi.deleteOfferTable(offer.id);

            if (
              result?.code ||
              result?.httpCode ||
              result?.error ||
              result?.status === 500
            ) {
              showErrorToast(
                result?.message ||
                  result?.error?.message ||
                  "Error trying to delete the offer",
              );
              return;
            }

            showSuccessToast("Offer Deleted");
            await refreshOffersList();
          } catch (error) {
            console.error("Failed to delete offer:", error);
            showErrorToast(error?.message || "Failed to delete offer.");
          } finally {
            setLoadingFalse();
          }
        },
      );
    };

    const onPushOrUpdateOffer = (offer) => {
      const isUpdate = offer.offerTableRowRlId !== null;

      openConfirmationDialog(
        "Push/Update Selected Offers to RL",
        "Confirm",
        [
          "Are you sure you want to push/update all selected offers to RightsLine?",
        ],
        async () => {
          setLoadingTrue();
          try {
            const result = await tapApi.pushOfferTable(offer.id);

            if (
              result?.code ||
              result?.httpCode ||
              result?.error ||
              result?.status === 500
            ) {
              showErrorToast(
                result?.message ||
                  result?.error?.message ||
                  "Failed to push offer.",
              );
              return;
            }

            showSuccessToast(isUpdate ? "Offer Updated" : "Offer Pushed to RL");
            await refreshOffersList();
          } catch (error) {
            console.error("Failed to push offer:", error);
            showErrorToast(error?.message || "Failed to push offer.");
          } finally {
            setLoadingFalse();
          }
        },
      );
    };

    const shouldRenderDelete = () => userCanManageOffers;

    const shouldRenderPush = (data) => {
      if (!userCanManageOffers) return false;
      const tapOfferStatus = data?.tapOfferStatus;
      const isInEditMode = editingRowsRef.current[data?.id];
      if (isInEditMode) return false;
      return PUSHABLE_STATUSES.includes(tapOfferStatus);
    };

    /** Sorts language options with "All Languages" always first */
    const sortedSubDubLanguageOptions = useMemo(() => {
      const options = offerSubDubLanguageOptions || [];
      if (options.length === 0) return options;

      // Sort alphabetically, but ensure "All Languages" is always first
      return [...options].sort((a, b) => {
        const aLabel = a?.label?.toLowerCase() || "";
        const bLabel = b?.label?.toLowerCase() || "";

        if (aLabel === "all languages") return -1;
        if (bLabel === "all languages") return 1;

        return aLabel.localeCompare(bLabel);
      });
    }, [offerSubDubLanguageOptions]);

    const columnConfig = useMemo(
      () => ({
        onDeleteOffer,
        onPushOrUpdateOffer,
        getOfferHistoryHref,
        onEditOffer,
        onSaveOffer,
        onCancelEdit,
        getEditingRows,
        getEditedValues,
        onValueChange,
        subDubLanguageOptions: sortedSubDubLanguageOptions,
        shouldRenderDelete,
        shouldRenderPush,
        onLanguageSelect,
        getLastSelectedLanguages,
        isTprJob,
      }),
      [
        onDeleteOffer,
        onPushOrUpdateOffer,
        getOfferHistoryHref,
        onEditOffer,
        onSaveOffer,
        onCancelEdit,
        getEditingRows,
        getEditedValues,
        onValueChange,
        sortedSubDubLanguageOptions,
        shouldRenderDelete,
        shouldRenderPush,
        onLanguageSelect,
        getLastSelectedLanguages,
        isTprJob,
      ],
    );

    const { columns, sortedColId, sortDirection } = useOffersGridUtils({
      columnConfig,
    });

    /** Client-side sorting of current page for grid display */
    const sortedOffers = useMemo(
      () => sortOfferRows(displayOffers, sortedColId, sortDirection),
      [displayOffers, sortedColId, sortDirection],
    );

    /** Full filtered set sorted for export/print */
    const sortedFilteredOffers = useMemo(
      () => sortOfferRows(filteredOffersWithEdits, sortedColId, sortDirection),
      [filteredOffersWithEdits, sortedColId, sortDirection],
    );

    const fetchAllRows = useCallback(
      async () => filteredOffers ?? [],
      [filteredOffers],
    );

    const {
      selectedRows,
      selectAllPages,
      deselectedRowIds,
      bannerText,
      onSelectionChanged,
      onRowSelected,
      clearSelection,
      resolveSelectedRows,
    } = usePaginatedGridSelection({
      totalItems: filteredOffers?.length ?? 0,
      fetchAllRows,
      entityName: "offer",
      rowData: sortedOffers ?? [],
      gridApiRef,
      resetKey: `${filteredOffers?.length}-${JSON.stringify(internalFilters)}-${searchAll}`,
    });

    const hasPushableOffers = useMemo(() => {
      if (selectAllPages) {
        return (filteredOffers ?? []).some(
          (offer) =>
            !deselectedRowIds.has(offer.id) &&
            PUSHABLE_STATUSES.includes(offer.tapOfferStatus),
        );
      }
      return selectedRows.some((offer) =>
        PUSHABLE_STATUSES.includes(offer.tapOfferStatus),
      );
    }, [selectAllPages, filteredOffers, deselectedRowIds, selectedRows]);

    const handleCloseBanner = () => {
      clearSelection();
    };

    const onBulkPushOffers = async () => {
      const resolvedRows = await resolveSelectedRows();
      const pushableOffers = resolvedRows.filter((offer) =>
        PUSHABLE_STATUSES.includes(offer.tapOfferStatus),
      );

      if (pushableOffers.length === 0) {
        showErrorToast("No selected offers are eligible for push.");
        return;
      }

      openConfirmationDialog(
        "Push/Update Selected Offers to RL",
        "Yes",
        [
          "Are you sure you want to push/update all selected offers to RightsLine?",
        ],
        async () => {
          setLoadingTrue();
          try {
            const jobId = await tapApi.pushOfferTableBulk(
              pushableOffers.map((o) => o.id),
            );

            if (jobId && typeof jobId !== "object") {
              handleCloseBanner();
              const onJobProgress = createJobProgressToastHandler(jobId);
              startJobStatusTracker(
                jobId,
                async (message, status) => {
                  onJobProgress(message, status);
                  if (status === "success") {
                    await refreshOffersList();
                  }
                  setLoadingFalse();
                },
                (jobTasks, jobInfo) => {
                  if (
                    jobInfo?.jobStatus === "COMPLETE" &&
                    (jobInfo?.errorCount ?? 0) === 0
                  ) {
                    return;
                  }
                  updateOffers({
                    isOffersUploadTasksDrawerOpen: true,
                    offersUploadJobTasks: jobTasks || [],
                  });
                },
              );
            } else {
              showErrorToast(
                jobId?.statusText ||
                  jobId?.message ||
                  jobId?.error?.message ||
                  "Failed to push offers.",
              );
              setLoadingFalse();
            }
          } catch (error) {
            console.error("Failed to bulk push offers:", error);
            showErrorToast(error?.message || "Failed to push offers.");
            setLoadingFalse();
          }
        },
      );
    };

    const onBulkDeleteOffers = async () => {
      const resolvedRows = await resolveSelectedRows();
      if (resolvedRows.length === 0) {
        return;
      }

      openConfirmationDialog(
        "Delete Offer(s)",
        "Yes",
        [
          "Are you sure you want to delete the offer(s) from TAP2 and Rightsline? This action cannot be undone.",
        ],
        async () => {
          setLoadingTrue();
          try {
            const jobId = await tapApi.deleteOfferTableBulk(
              resolvedRows.map((o) => o.id),
            );

            if (jobId && typeof jobId !== "object") {
              handleCloseBanner();
              const onJobProgress = createJobProgressToastHandler(jobId);
              startJobStatusTracker(
                jobId,
                async (message, status) => {
                  onJobProgress(message, status);
                  if (status === "success") {
                    await refreshOffersList();
                  }
                  setLoadingFalse();
                },
                (jobTasks, jobInfo) => {
                  if (
                    jobInfo?.jobStatus === "COMPLETE" &&
                    (jobInfo?.errorCount ?? 0) === 0
                  ) {
                    return;
                  }
                  updateOffers({
                    isOffersUploadTasksDrawerOpen: true,
                    offersUploadJobTasks: jobTasks || [],
                  });
                },
              );
            } else {
              showErrorToast(
                jobId?.statusText ||
                  jobId?.message ||
                  jobId?.error?.message ||
                  "Failed to delete offers.",
              );
              setLoadingFalse();
            }
          } catch (error) {
            console.error("Failed to bulk delete offers:", error);
            showErrorToast(error?.message || "Failed to delete offers.");
            setLoadingFalse();
          }
        },
      );
    };

    const handleExport = async () => {
      const dataToExport = sortedFilteredOffers ?? [];
      if (dataToExport.length === 0) {
        showErrorToast("No data to export");
        return;
      }

      const exportColumns = getOfferTableExcelExportColumns(hasEpisodeFeature);

      const fileName = selectedJob?.id
        ? `Offers_Job_${selectedJob.id}_${moment().format("YYYY-MM-DD")}`
        : `Offers_${moment().format("YYYY-MM-DD")}`;

      try {
        await exportAsExcelFile(dataToExport, fileName, exportColumns);
        showSuccessToast("Export completed successfully");
      } catch (error) {
        console.error("Export failed:", error);
        showErrorToast("Failed to export data");
      }
    };

    const handlePrint = () => {
      const dataToprint = sortedFilteredOffers ?? [];
      if (dataToprint.length === 0) {
        showErrorToast("No data to print");
        return;
      }

      const visibleFields = columns
        .filter((col) => col.field && !hiddenColumns.includes(col.field))
        .map((col) => ({
          field: col.field,
          headerName: col.headerName || col.field,
        }));

      const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Offers - Job ${escapeHtml(selectedJob?.id || "")}</title>
          <style>
            body { font-family: Arial, sans-serif; font-size: 10px; margin: 20px; }
            h1 { font-size: 16px; margin-bottom: 10px; }
            .info { font-size: 11px; color: #666; margin-bottom: 15px; }
            table { border-collapse: collapse; width: 100%; }
            th, td { border: 1px solid #ddd; padding: 4px 6px; text-align: left; }
            th { background-color: #f5f5f5; font-weight: bold; }
            tr:nth-child(even) { background-color: #fafafa; }
            @media print {
              body { margin: 0; }
              table { font-size: 9px; }
            }
          </style>
        </head>
        <body>
          <h1>Offers${selectedJob?.id ? ` - Job ${escapeHtml(selectedJob.id)}` : ""}</h1>
          <div class="info">
            Printed: ${moment().format("YYYY-MM-DD HH:mm")} | 
            Total Records: ${escapeHtml(dataToprint.length)}
            ${searchAll ? ` | Search: "${escapeHtml(searchAll)}"` : ""}
          </div>
          <table>
            <thead>
              <tr>
                ${visibleFields.map((col) => `<th>${escapeHtml(col.headerName)}</th>`).join("")}
              </tr>
            </thead>
            <tbody>
              ${dataToprint
                .map(
                  (row) => `
                <tr>
                  ${visibleFields
                    .map((col) => {
                      const value = row[col.field];
                      return `<td>${escapeHtml(value)}</td>`;
                    })
                    .join("")}
                </tr>
              `,
                )
                .join("")}
            </tbody>
          </table>
        </body>
      </html>
    `;

      const printWindow = window.open("", "_blank");
      if (printWindow) {
        printWindow.document.write(printContent);
        printWindow.document.close();
        printWindow.focus();
        printWindow.print();
      }
    };

    // Expose methods and data to parent via ref
    React.useImperativeHandle(
      ref,
      () => ({
        handleExport,
        handlePrint,
        COLUMN_OPTIONS,
        filteredOffers,
      }),
      [
        columns,
        hiddenColumns,
        sortedFilteredOffers,
        filteredOffers,
        selectedJob,
        searchAll,
        hasEpisodeFeature,
      ],
    );

    /** Filters columns by visibility and adds expand column for master-detail */
    const visibleColumns = useMemo(() => {
      const filtered = columns.filter((col) => {
        if (!col.field) return true;
        return !hiddenColumns.includes(col.field);
      });

      const expandColumn = {
        headerName: "",
        field: "expand",
        width: 50,
        minWidth: 50,
        maxWidth: 50,
        cellRenderer: "agGroupCellRenderer",
        suppressHeaderMenuButton: true,
        sortable: false,
        resizable: false,
        pinned: "left",
      };

      return [expandColumn, ...filtered];
    }, [columns, hiddenColumns]);

    return (
      <div className={styles.gridContainer}>
        <div className={styles.gridMain}>
          {bannerText && (
            <Banner
              faPackage="far"
              iconTypePrimary="fa-check-circle"
              role="status"
              type="success"
              onClose={handleCloseBanner}
              className="selection-banner"
            >
              <ContentBlock className="banner-content">
                <Typography className="main-banner-text" variant="T20">
                  {bannerText}
                </Typography>
                <div className="banner-actions">
                  {userCanManageOffers && hasPushableOffers && (
                    <Button
                      className="bulk-action-btn"
                      iconType="fa-solid fa-upload"
                      type="button"
                      styleType="link"
                      onClick={onBulkPushOffers}
                    >
                      PUSH/UPDATE TO RL
                    </Button>
                  )}
                  {userCanManageOffers && (
                    <Button
                      className="bulk-action-btn-danger"
                      iconType="fa-solid fa-trash"
                      type="button"
                      styleType="link"
                      onClick={onBulkDeleteOffers}
                    >
                      DELETE OFFER(S)
                    </Button>
                  )}
                </div>
              </ContentBlock>
            </Banner>
          )}

          <DataGrid
            className={styles.dataGrid}
            dataGridApiRef={gridApiRef}
            agGrid={{
              rowData: sortedOffers ?? [],
              headerHeight: 45,
              // Allow native browser context menu on links (e.g. Offer history → new tab)
              suppressContextMenu: true,
              suppressColumnStateReset: true,
              onSelectionChanged,
              onRowSelected,
              // Preserve scroll position on state changes
              getRowId: (params) => String(params.data.id),
              suppressScrollOnNewData: true,
              // Master Detail configuration
              masterDetail: true,
              detailRowAutoHeight: true,
              detailCellRenderer: MessageDetailCellRenderer,
              isRowMaster: (dataItem) =>
                dataItem?.message != null && dataItem?.message !== "",
            }}
            columnDefs={visibleColumns}
            defaultColumnDef={{
              wrapText: true,
              autoHeight: true,
              sortable: true,
              resizable: true,
              suppressHeaderMenuButton: true,
              cellStyle: {
                paddingTop: "10px",
                paddingBottom: "10px",
                wordBreak: "break-word",
              },
            }}
            selectable
            selectMode="multiple"
            bordered="none"
            striped
            autoSizing
            size="default"
            sortable
            noResultsMessage="No Offers Found"
          />
        </div>
      </div>
    );
  },
);
